import * as React from 'react'
import styled from 'styled-components'
import { IconTableFilter, IconTableArrowLeft, IconTableArrowRight } from '../../assets/icons/IconGroup'
import classNames from '../_util/classNames'

const Wrapper = styled.div`
  min-height: 320px;
  table {
    width: 100%;
    border-collapse: separate;
    th,
    td {
      vertical-align: middle;
      /* text-align: right; */
      text-align: left;
    }
    th:nth-child(1),
    td:nth-child(1) {
      /* text-align: start; */
      padding-left: 16px;
    }
    th {
      height: 60px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      color: rgba(255, 255, 255, 0.65);
      font-size: 14px;
      font-weight: 400;
      line-height: 100%;
    }
    td {
      height: 64px;
      color: #fff;
      font-size: 16px;
      font-weight: 500;
      line-height: 100%;
    }
  }
  .filter {
    margin-left: 4px;
    width: 20px;
    height: 20px;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 4px;
    user-select: none;
    cursor: pointer;
    path {
      fill: rgba(255, 255, 255, 0.45);
      transition: all 0.3s ease-in-out;
    }
    &.unknown {
      transition: all 0.3s ease-in-out;
      &:hover {
        opacity: 0.6;
      }
    }
    &.start {
      path:nth-child(1) {
        fill: #ffc9b1;
      }
    }
    &.end {
      path:nth-child(2) {
        fill: #ffc9b1;
      }
    }
  }
  .flip {
    margin-top: 26px;
    .page {
      padding: 8px 16px;
      gap: 10px;
      border-radius: 12px 12px 0px 0px;
      background: #292929;
    }
    svg {
      user-select: none;
      transition: all 0.3s ease-in-out;
    }
    .active {
      fill: #cdccc6;
      cursor: pointer;
      &:hover {
        opacity: 0.6;
      }
    }
    .default {
      fill: #6b6a64;
      cursor: not-allowed;
    }
    p {
      color: #cdccc6;
      font-size: 14px;
    }
  }
  .noData {
    img {
      width: 112px;
      height: 112px;
    }
    p {
      margin-top: 12px;
      color: #fff;
      font-size: 16px;
      line-height: 100%;
    }
  }
`

type filterStatesType = {
  [key: string]: 'unknown' | 'start' | 'end'
}

export interface TableObjectType {
  key: React.Key
  [key: string]: any
}

export interface ColumnObjectType extends TableObjectType {
  onFilter?: (key: string, state: 'unknown' | 'start' | 'end') => void
}

export interface TbodyObjectType extends TableObjectType {
  onTbodyClick?: (item: any) => void
}

export interface TableProps {
  className?: string
  loading?: boolean
  pageSize?: number
  columns?: ColumnObjectType[]
  dataSource?: TbodyObjectType[]
  onFlip?: (currentPage: number) => void
}

const Table: React.FC<TableProps> = (props: TableProps) => {
  const { className, loading, pageSize = 10, columns = [], dataSource = [], onFlip } = props

  const classes = classNames(className)

  const filterTotal = React.useMemo(() => {
    const total = dataSource.length
    const totalPage = Math.ceil(total / pageSize)
    return { total, totalPage }
  }, [dataSource, pageSize])

  const [filterStates, setFilterStates] = React.useState<filterStatesType>(
    columns.reduce((acc, { key, onFilter }) => {
      if (onFilter) {
        // eslint-disable-next-line no-param-reassign
        acc[key as keyof filterStatesType] = 'unknown'
      }
      return acc
    }, {} as filterStatesType),
  )

  const handleFilterClick = (key: string, onFilter: ColumnObjectType['onFilter']) => {
    setFilterStates((prevStates) => {
      const state = prevStates[key] === 'unknown' ? 'start' : prevStates[key] === 'start' ? 'end' : 'unknown'
      onFilter?.(key, state)
      return { ...prevStates, [key]: state }
    })
  }

  const [currentPage, setCurrentPage] = React.useState<number>(1)

  const datas = React.useMemo(() => {
    return dataSource.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  }, [dataSource, currentPage, pageSize])

  React.useEffect(() => {
    onFlip?.(currentPage)
  }, [currentPage, onFlip])

  return (
    <Wrapper className={classes}>
      {!loading && (
        <table>
          <thead>
            <tr>
              {columns.map(({ key, title, onFilter }) => {
                return (
                  <th key={key}>
                    <div className={classNames({ 'row-end': onFilter })}>
                      {title}
                      {onFilter && (
                        <div className={classNames('filter', filterStates[key as string])}>
                          <IconTableFilter
                            onClick={() => {
                              handleFilterClick(key as string, onFilter)
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {datas.map((ele) => (
              <tr
                key={ele.key}
                onClick={() => {
                  ele.onTbodyClick?.(ele)
                }}
              >
                {Object.entries(ele).map(([key, value]) => {
                  if (key === 'key' || key === 'onTbodyClick') {
                    return null
                  }
                  return (
                    <td key={key}>
                      <div>{value}</div>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* {!loading && filterTotal.totalPage > 1 && (
        <div className="flip flex flex-row items-end justify-center">
          <div className="page row-center">
            <IconTableArrowLeft
              className={`${currentPage > 1 ? 'active' : 'default'}`}
              onClick={() => {
                if (currentPage > 1) {
                  setCurrentPage((v) => v - 1);
                }
              }}
            />
            <p>
              Page {currentPage} of {filterTotal.totalPage}
            </p>
            <IconTableArrowRight
              className={`${currentPage < filterTotal.totalPage ? 'active' : 'default'}`}
              onClick={() => {
                if (filterTotal.totalPage > currentPage) {
                  setCurrentPage((v) => v + 1);
                }
              }}
            />
          </div>
        </div>
      )} */}
      {!loading && filterTotal.total === 0 && (
        <div className="noData col-center">
          <img src="/images/_global/noData.png" alt="icon" />
          <p>No Data</p>
        </div>
      )}
    </Wrapper>
  )
}

export default Table
